import { Box, Text, Select, Flex } from '@chakra-ui/react';
import useChartTheme from '../useChartTheme';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { getMonthlyDeposits, MonthlyDeposit } from '../../../api/AdminAPI';

const MonthlyDepositsChart = () => {
  const [data, setData] = useState<MonthlyDeposit[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<number>(11);
  
  const token = useSelector((state: RootState) => state.userData.apiKey);

  const {
    series: [series],
    grid,
    tick,
    tooltip,
    legend,
  } = useChartTheme(['chart.2']);

  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      
      setIsLoading(true);
      setError(null);
      
      const response = await getMonthlyDeposits(token, period, 0);
      
      if (response.success && response.data) {
        // Sort data by date (oldest to newest)
        const sortedData = [...response.data].sort((a, b) => {
          const dateA = new Date(a.date + '-01'); // Add day for parsing "2024-12"
          const dateB = new Date(b.date + '-01');
          return dateA.getTime() - dateB.getTime();
        });
        setData(sortedData);
      } else {
        setError(response.error || 'Failed to load data');
      }
      
      setIsLoading(false);
    };

    fetchData();
  }, [token, period]);

  if (error) {
    return (
      <Box p={4} borderRadius="md" bg="status.errorSubtle" color="status.error">
        <Text>Error loading data: {error}</Text>
      </Box>
    );
  }

  return (
    <Box className="chart-container" p={6} bg="bg.surface" borderRadius="lg" boxShadow="sm" border="1px solid" borderColor="border.subtle">
      <Flex justify="space-between" align="center" mb={4} flexDir={{ base: 'column', sm: 'row' }} gap={{ base: 2, sm: 0 }}>
        <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="bold">
          💰 Monthly Deposits
        </Text>
        <Select
          value={period}
          onChange={(e) => setPeriod(Number(e.target.value))}
          width={{ base: '100%', sm: '150px' }}
          size="sm"
        >
          <option value={6}>6 months</option>
          <option value={11}>12 months</option>
          <option value={23}>24 months</option>
        </Select>
      </Flex>

      {isLoading ? (
        <Flex justify="center" align="center" height="300px">
          <Text color="text.secondary">Loading data...</Text>
        </Flex>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" {...grid} />
            <XAxis 
              dataKey="date" 
              tick={tick}
              tickFormatter={(value) => {
                const [year, month] = value.split('-');
                return `${month}/${year.slice(2)}`;
              }}
            />
            <YAxis tick={tick} />
            <Tooltip
              {...tooltip}
              formatter={(value: number) => [`$${value.toFixed(2)}`, 'USD']}
            />
            <Legend {...legend} />
            <Bar
              dataKey="usd_sum"
              name="Deposits (USD)"
              fill={series}
              radius={[8, 8, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </Box>
  );
};

export default MonthlyDepositsChart;

